import { AnimatePresence, motion } from 'framer-motion';
import {
  Briefcase,
  Calendar,
  Camera,
  Clock,
  Key,
  Mail,
  MapPin,
  Phone,
  Settings,
  ShieldAlert,
  ShieldCheck,
  User,
  Wallet,
} from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ProfilePhotoUpload } from '@/components/profile';
import { useAuth } from '@/hooks';
import { userService } from '@/services';
import { useAuthStore } from '@/store';
import {
  extractErrorMessage,
  formatDate,
  formatNaira,
  formatStatus,
  getAccountStatusColor,
} from '@/utils';
import { cn } from '@/utils/cn';
import { formatEmployeeId } from '@/utils/formatters';
import { showErrorToast, showSuccessToast } from '@/utils/toast-helpers';

export function Profile() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [isEditing, setIsEditing] = useState(false);
  const [photoUploadOpen, setPhotoUploadOpen] = useState(false);
  const [editData, setEditData] = useState(() => ({
    phoneNumber: user?.phoneNumber ?? '',
    address: user?.profile?.address ?? '',
    emergencyContact: user?.profile?.emergencyContact ?? {
      name: '',
      phone: '',
      relationship: '',
    },
  }));

  const handleUpdateProfile = async () => {
    try {
      alert('Profile update functionality coming soon');
      setIsEditing(false);
    } catch (error) {
      alert('Failed to update profile');
    }
  };

  const handlePhotoUpload = async (base64Image: string) => {
    try {
      const updatedUser = await userService.updateProfilePhoto(base64Image);
      useAuthStore.getState().setUser(updatedUser);
      showSuccessToast('Profile photo updated successfully');
    } catch (error) {
      showErrorToast(extractErrorMessage(error, 'Failed to update profile photo'));
      throw error;
    }
  };

  if (!user) return null;

  const sections = [
    {
      title: 'Personal Information',
      icon: User,
      items: [
        {
          label: 'Full Name',
          value: `${user.profile.firstName} ${user.profile.middleName || ''} ${user.profile.lastName}`,
          icon: User,
        },
        { label: 'Employee ID', value: formatEmployeeId(user.employeeId), icon: ShieldCheck },
        { label: 'Username', value: `@${user.username}`, icon: Mail },
        {
          label: 'Phone Number',
          value: isEditing ? (
            <input
              type="text"
              value={editData.phoneNumber}
              onChange={(e) => setEditData({ ...editData, phoneNumber: e.target.value })}
              className="w-full bg-neolab-green/5 dark:bg-white/5 border-none rounded-lg px-2 py-1 text-sm font-bold focus:ring-1 focus:ring-neolab-green outline-none"
            />
          ) : (
            user.phoneNumber
          ),
          icon: Phone,
        },
        {
          label: 'Department',
          value: user.profile.department,
          icon: Briefcase,
        },
        { label: 'Job Title', value: user.profile.jobTitle, icon: Briefcase },
        {
          label: 'Date of Hire',
          value: user.profile.dateOfHire ? formatDate(user.profile.dateOfHire) : 'Not set',
          icon: Calendar,
        },
        {
          label: 'Address',
          value: isEditing ? (
            <textarea
              value={editData.address}
              onChange={(e) => setEditData({ ...editData, address: e.target.value })}
              className="w-full bg-neolab-green/5 dark:bg-white/5 border-none rounded-lg px-2 py-1 text-sm font-bold focus:ring-1 focus:ring-neolab-green outline-none resize-none"
              rows={2}
            />
          ) : (
            user.profile.address || 'Not set'
          ),
          icon: MapPin,
          fullWidth: true,
        },
      ],
    },
    {
      title: 'Attendance Settings',
      icon: Settings,
      items: [
        {
          label: 'Mon-Fri Clock-In',
          value: user.attendanceConfig.monToFriClockInTime,
          icon: Clock,
        },
        {
          label: 'Normal Saturday',
          value: user.attendanceConfig.normalSatClockInTime,
          icon: Clock,
        },
        {
          label: 'Last Saturday',
          value: user.attendanceConfig.lastSatOfMonthClockInTime,
          icon: Clock,
        },
        {
          label: 'Grace Period',
          value: `${user.attendanceConfig.gracePeriodMinutes} mins`,
          icon: Clock,
        },
        {
          label: 'Late Arrival Fee',
          value: formatNaira(user.attendanceConfig.lateArrivalFee),
          icon: Wallet,
        },
        {
          label: 'Extra Late Fee Per Interval',
          value: formatNaira(user.attendanceConfig.extraLateFeePerInterval),
          icon: Wallet,
        },
        {
          label: 'Extra Late Fee Interval',
          value: `${user.attendanceConfig.extraLateFeeIntervalMinutes} mins`,
          icon: Clock,
        },
        {
          label: 'Missed Clock-Out',
          value: formatNaira(user.attendanceConfig.missedClockOutFee),
          icon: Wallet,
        },
        {
          label: 'Missed Clock-In',
          value: formatNaira(user.attendanceConfig.missedClockInFee || 0),
          icon: Wallet,
        },
        {
          label: 'Late Fees',
          value: user.attendanceConfig.lateFeesEnabled ? 'Enabled' : 'Disabled',
          icon: ShieldAlert,
        },
        {
          label: 'Clock-Out Required',
          value: user.attendanceConfig.clockOutRequired ? 'Yes' : 'No',
          icon: ShieldCheck,
        },
      ],
    },
  ];

  return (
    <div className="space-y-10 max-w-5xl mx-auto">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
          <h1 className="text-4xl font-black text-slate-900 dark:text-white tracking-tight">
            My Profile
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-2 font-medium">
            Manage your personal details and account preferences.
          </p>
        </motion.div>

        <div className="flex items-center gap-4">
          {isEditing ? (
            <>
              <button
                type="button"
                onClick={() => setIsEditing(false)}
                className="px-6 py-3 rounded-2xl bg-slate-100 dark:bg-white/5 text-slate-600 dark:text-slate-300 font-black text-sm"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleUpdateProfile}
                className="px-6 py-3 rounded-2xl bg-neolab-green text-white font-black text-sm shadow-xl"
              >
                Save Changes
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => setIsEditing(true)}
              className="px-6 py-3 rounded-2xl bg-white dark:bg-white/5 text-neolab-green font-black text-sm border-2 border-neolab-green/20"
            >
              Edit Profile
            </button>
          )}
          <button
            type="button"
            onClick={() => navigate('/change-password')}
            className="flex items-center gap-2 px-6 py-3 rounded-2xl bg-gradient-brand text-white font-black text-sm shadow-xl hover:scale-[1.02] active:scale-95 transition-all border-b-2 border-neolab-dark-green"
          >
            <Key className="w-4 h-4" />
            Change Password
          </button>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative p-10 rounded-[3rem] glass-card dark:glass-card-dark shadow-sm overflow-hidden"
      >
        <div className="absolute top-0 right-0 w-64 h-64 bg-neolab-green/5 rounded-full -mr-20 -mt-20 blur-3xl" />
        <div className="relative z-10 flex flex-col md:flex-row items-center gap-10">
          <div className="relative group">
            <div className="w-32 h-32 rounded-[2.5rem] bg-neolab-green/10 flex items-center justify-center text-4xl font-black text-neolab-green border-4 border-white dark:border-white/5 shadow-2xl">
              {user.profile.profilePhoto ? (
                <img
                  src={user.profile.profilePhoto}
                  alt="Profile"
                  className="w-full h-full object-cover rounded-[2.5rem]"
                />
              ) : (
                <span>
                  {user.profile.firstName[0]}
                  {user.profile.lastName[0]}
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={() => setPhotoUploadOpen(true)}
              className="absolute -bottom-2 -right-2 p-3 rounded-2xl bg-white dark:bg-neolab-deep-green shadow-xl border border-slate-100 dark:border-white/10 text-slate-400 hover:text-neolab-green transition-colors cursor-pointer"
            >
              <Camera className="w-5 h-5" />
            </button>
          </div>

          <div className="text-center md:text-left space-y-4">
            <div>
              <h2 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight">
                {user.profile.firstName} {user.profile.lastName}
              </h2>
              <p className="text-lg font-bold text-neolab-green">{user.profile.jobTitle}</p>
            </div>
            <div className="flex flex-wrap justify-center md:justify-start gap-3">
              <div
                className={cn(
                  'px-4 py-1.5 rounded-full text-xs font-black uppercase tracking-widest border',
                  getAccountStatusColor(user.accountStatus) === 'green'
                    ? 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20'
                    : 'bg-rose-500/10 text-rose-600 border-rose-500/20',
                )}
              >
                {formatStatus(user.accountStatus)}
              </div>
              <div className="px-4 py-1.5 rounded-full text-xs font-black uppercase tracking-widest bg-blue-500/10 text-blue-600 border border-blue-500/20">
                {formatStatus(user.role)}
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {sections.map((section, idx) => (
          <motion.div
            key={section.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 + idx * 0.1 }}
            className="rounded-[2.5rem] glass-card dark:glass-card-dark shadow-sm overflow-hidden"
          >
            <div className="px-8 py-6 border-b border-slate-100 dark:border-slate-800 flex items-center gap-4 bg-slate-50/50 dark:bg-slate-800/20">
              <div className="p-2.5 rounded-xl bg-white dark:bg-slate-800 shadow-sm">
                <section.icon className="w-5 h-5 text-neolab-green" />
              </div>
              <h3 className="text-xl font-black text-slate-900 dark:text-white tracking-tight">
                {section.title}
              </h3>
            </div>
            <div className="p-8 grid grid-cols-1 sm:grid-cols-2 gap-8">
              {section.items.map((item, i) => (
                <div key={i} className={cn('space-y-1.5', item.fullWidth && 'sm:col-span-2')}>
                  <p className="text-xs font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                    <item.icon className="w-3 h-3" />
                    {item.label}
                  </p>
                  <p className="text-sm font-bold text-slate-700 dark:text-slate-200">
                    {item.value}
                  </p>
                </div>
              ))}
            </div>
          </motion.div>
        ))}
      </div>

      {user.profile.emergencyContact && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="rounded-[3rem] bg-white dark:bg-gradient-dark text-slate-900 dark:text-white p-10 shadow-xl shadow-rose-500/5 dark:shadow-none relative overflow-hidden border border-slate-100 dark:border-none"
        >
          <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-slate-50 dark:from-accent-rose/10 to-transparent" />
          <div className="relative z-10">
            <div className="flex items-center gap-4 mb-8">
              <div className="p-3 rounded-2xl bg-white/10 dark:bg-accent-rose/10 backdrop-blur-md">
                <ShieldAlert className="w-6 h-6 text-rose-400" />
              </div>
              <h3 className="text-2xl font-black tracking-tight">Emergency Contact</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
              <div>
                <p className="text-xs font-black text-white/40 uppercase tracking-widest mb-1">
                  Contact Name
                </p>
                {isEditing ? (
                  <input
                    type="text"
                    value={editData.emergencyContact.name}
                    onChange={(e) =>
                      setEditData({
                        ...editData,
                        emergencyContact: { ...editData.emergencyContact, name: e.target.value },
                      })
                    }
                    className="w-full bg-white/10 border-none rounded-lg px-3 py-2 text-lg font-bold focus:ring-1 focus:ring-white outline-none"
                  />
                ) : (
                  <p className="text-lg font-bold">{user.profile.emergencyContact.name}</p>
                )}
              </div>
              <div>
                <p className="text-xs font-black text-white/40 uppercase tracking-widest mb-1">
                  Phone Number
                </p>
                {isEditing ? (
                  <input
                    type="text"
                    value={editData.emergencyContact.phone}
                    onChange={(e) =>
                      setEditData({
                        ...editData,
                        emergencyContact: { ...editData.emergencyContact, phone: e.target.value },
                      })
                    }
                    className="w-full bg-white/10 border-none rounded-lg px-3 py-2 text-lg font-bold focus:ring-1 focus:ring-white outline-none"
                  />
                ) : (
                  <p className="text-lg font-bold">{user.profile.emergencyContact.phone}</p>
                )}
              </div>
              <div>
                <p className="text-xs font-black text-white/40 uppercase tracking-widest mb-1">
                  Relationship
                </p>
                {isEditing ? (
                  <input
                    type="text"
                    value={editData.emergencyContact.relationship}
                    onChange={(e) =>
                      setEditData({
                        ...editData,
                        emergencyContact: {
                          ...editData.emergencyContact,
                          relationship: e.target.value,
                        },
                      })
                    }
                    className="w-full bg-white/10 border-none rounded-lg px-3 py-2 text-lg font-bold focus:ring-1 focus:ring-white outline-none"
                  />
                ) : (
                  <p className="text-lg font-bold">{user.profile.emergencyContact.relationship}</p>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      )}

      <AnimatePresence>
        {photoUploadOpen && (
          <ProfilePhotoUpload
            onUpload={handlePhotoUpload}
            onClose={() => setPhotoUploadOpen(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

export default Profile;
